/*
 * ---------------------------------------------------------------------------
 * Tripster Developers - Gift card "Buy Now" button
 * ---------------------------------------------------------------------------
 * Self-contained add-to-cart element for the gift card product template.
 *
 * It is intentionally NOT a <product-form>: product-info.js picks up the first
 * <product-form> in the product info column and rewrites its button text to
 * window.variantStrings.addToCart ("Create your magnets") whenever the shopper
 * changes a variant. Staying out of that element keeps the "Buy Now" label.
 *
 * The selected denomination is read from the theme's variant picker at click
 * time, because product-info.js replaces the whole <variant-selects> node (and
 * the JSON inside it) on every variant change.
 * ---------------------------------------------------------------------------
 */
if (!customElements.get('gift-card-buy-now')) {
  customElements.define(
    'gift-card-buy-now',
    class GiftCardBuyNow extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.variantInput = this.querySelector('.gift-card-buy-now__variant-id');
        this.submitButton = this.querySelector('.gift-card-buy-now__submit');
        this.labelElement = this.querySelector('.gift-card-buy-now__label');
        this.spinner = this.querySelector('.loading__spinner');
        this.errorWrapper = this.querySelector('.gift-card-buy-now__error');
        this.errorText = this.querySelector('.gift-card-buy-now__error-text');
        this.defaultLabel = this.labelElement ? this.labelElement.textContent.trim() : 'Buy Now';
      }

      connectedCallback() {
        if (!this.form) return;
        this.variantInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.dataset.sectionId) return;
            this.syncVariant(event.data.variant);
          });
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      /* Reads the variant the theme's picker currently has selected. */
      getSelectedVariant() {
        const json = document.querySelector('variant-selects [data-selected-variant]');
        if (!json) return null;
        try {
          return JSON.parse(json.textContent);
        } catch (e) {
          return null;
        }
      }

      syncVariant(variant) {
        const selected = variant === undefined ? this.getSelectedVariant() : variant;

        if (!selected) {
          this.variantInput.value = '';
          this.toggleSubmitButton(true, window.variantStrings?.unavailable || 'Unavailable');
          return;
        }

        this.variantInput.value = selected.id;
        if (selected.available === false) {
          this.toggleSubmitButton(true, window.variantStrings?.soldOut || 'Sold out');
        } else {
          this.toggleSubmitButton(false);
        }
        return selected;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.labelElement) this.labelElement.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          /* Always our own label - never window.variantStrings.addToCart. */
          if (this.labelElement) this.labelElement.textContent = this.defaultLabel;
        }
      }

      onSubmitHandler(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const selected = this.syncVariant();
        if (!this.variantInput.value) {
          this.handleErrorMessage(window.variantStrings?.unavailable || 'Unavailable');
          return;
        }
        if (selected && selected.available === false) return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.spinner?.classList.remove('hidden');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'gift-card-buy-now',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'gift-card-buy-now',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            if (cartEl && cartEl.classList.contains('is-empty')) cartEl.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.spinner?.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (!this.errorWrapper) return;
        this.errorWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorText.textContent = errorMessage;
      }
    }
  );
}
